import React, { FC } from 'react';
import { Link } from 'gatsby';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import cx from 'classnames';
import { startCase } from 'lodash';
import CrossIcon from '@/assets/icons/cross.svg';
import Button, { ButtonVariant } from '@/components/Common/Button';
import * as styles from './Toast.module.scss';

export enum ToastVariant {
  Extended = 'extended',
  Regular = 'regular',
  Error = 'error',
}

export type ToastMessageContent = {
  link?: [path: string, label: string];
  subtext?: string;
  text: string;
  type?: ToastVariant;
};

export type ToastMessage = ToastMessageContent & {
  id: string;
};

type MessageProps = {
  variant?: ToastVariant;
  message: ToastMessage;
  onClear: () => void;
};

export const Message: FC<MessageProps> = ({
  message,
  variant = ToastVariant.Regular,
  onClear,
}) => (
  <div
    role="alert"
    className={cx(styles.message, styles[`message${startCase(variant)}`])}
  >
    <div>
      <p className={styles.text}>{message.text}</p>
      {message.subtext ? <div className={styles.subtext}>{message.subtext}</div> : null}
    </div>

    {message.link && variant === ToastVariant.Extended ? (
      <Button
        as={Link}
        to={message.link[0]}
        className={styles.link}
        onClick={onClear}
        variant={ButtonVariant.Primary}
      >
        {message.link[1]}
      </Button>
    ) : null}

    <Button variant={ButtonVariant.Bare} className={styles.clearButton} onClick={onClear}>
      <CrossIcon />
    </Button>
  </div>
);

type Props = {
  messages: ToastMessage[];
  onClear: (message: ToastMessage) => void;
};

const Toast: FC<Props> = ({ messages, onClear }) => (
  <div className={styles.main}>
    <TransitionGroup>
      {messages.map((message) => (
        <CSSTransition
          key={message.id}
          timeout={300}
          classNames={{
            enter: styles.enter,
            enterActive: styles.enterActive,
            exit: styles.exit,
            exitActive: styles.exitActive,
          }}
        >
          <Message
            message={message}
            variant={message.type}
            onClear={() => onClear(message)}
          />
        </CSSTransition>
      ))}
    </TransitionGroup>
  </div>
);

export default Toast;
